import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/content";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/areas", label: "Areas" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#0A192F]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-narrow flex items-center justify-between py-4 md:py-5">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logoUrl}
            alt="Declan Bishop Property Consultant"
            className="h-16 w-16 md:h-20 md:w-20 object-contain"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-[22px] md:text-[24px] text-brand-navy tracking-tight">
              Declan Bishop
            </span>
            <span className="text-[10px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              Property Consultant
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.18em] uppercase font-medium ink-link ${
                  isActive ? "text-brand-navy" : "text-brand-navy/70 hover:text-brand-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/contact"
            data-testid="navbar-cta-button"
            className="inline-flex items-center px-6 py-3 bg-brand-navy text-brand-cream text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
          >
            Request a Quote
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          aria-label="Open menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-brand-navy"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden border-t border-brand-navy/10 bg-brand-cream"
        >
          <div className="container-narrow py-8 flex flex-col gap-6">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base tracking-[0.18em] uppercase font-medium ${
                    isActive ? "text-brand-navy" : "text-brand-navy/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-navbar-cta"
              className="mt-4 inline-flex items-center justify-center px-6 py-3 bg-brand-navy text-brand-cream text-[12px] tracking-[0.22em] uppercase font-semibold"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
